import { DocumentReference, Timestamp } from "firebase/firestore";
// Declared in the shared filter module so the web app, the Cloud Function, and
// the system-trait templates share ONE declaration of this live Firestore map.
import type {
  ContainmentSuppressionOptions,
  ObjectDetectionDefaultOptions,
} from "@shared/containmentOverlapFilter";

export type { ContainmentSuppressionOptions, ObjectDetectionDefaultOptions };

export const ENTRY_CONFIG_TYPES = {
  TEXT: "text",
  NUMBER: "number",
  CATEGORY: "category",
  DATE: "date",
  BOOLEAN: "boolean",
  PERCENTAGE: "percentage",
  PHOTO: "photo",
  TRANSCRIPTION: "transcription",
  IMAGE_MEASUREMENT: "imageMeasurement",
  STAND_COUNT_MEASUREMENT: "standCountMeasurement",
  ROW_PLANTED_STAND_COUNT: "rowPlantedStandCount",
  RULER: "ruler",
  LEAF_AREA_INDEX: "leafAreaIndex",
  TIMER: "timer",
} as const;

export type EntryConfigType = (typeof ENTRY_CONFIG_TYPES)[keyof typeof ENTRY_CONFIG_TYPES];

// MARK: - Image Measurement Model Types

/**
 * The set of supported image model variants.
 * Mirrors Swift: ImageMeasurementModelOptions.Kind
 */
export const IMAGE_MEASUREMENT_MODEL_KINDS = {
  OPEN_SET_OBJECT_DETECTOR: "openSetObjectDetector",
  STRAWBERRY: "strawberry",
  APPLE: "apple",
  TOMATO: "tomato",
  BLUEBERRY: "blueberry",
  SPINACH_KEYPOINT: "spinachKeypoint",
  ARUGULA_KEYPOINT: "arugulaKeypoint",
  BISECTED_LETTUCE: "bisectedLettuce",
  SQUASH: "squash",
  CUCUMBER: "cucumber",
  NONE: "none",
} as const;

export type ImageMeasurementModelKind =
  (typeof IMAGE_MEASUREMENT_MODEL_KINDS)[keyof typeof IMAGE_MEASUREMENT_MODEL_KINDS];

export function isKeypointModel(kind: string): boolean {
  return kind === IMAGE_MEASUREMENT_MODEL_KINDS.SPINACH_KEYPOINT ||
         kind === IMAGE_MEASUREMENT_MODEL_KINDS.ARUGULA_KEYPOINT;
}

/**
 * Base across all model variants to provide a discriminant and common UI signal.
 * The on-wire JSON is a single flat object with `kind` and inline fields.
 */
export interface ImageMeasurementModelBase {
  /** Discriminator used for narrowing and validation. */
  kind: ImageMeasurementModelKind;
  /** Whether UI should show weight entry for this model. */
  hasWeightMeasurement: boolean;
}

/**
 * Open-set object detector model.
 * - `prompts` are required string labels to detect.
 * - `confidenceThreshold` gates detections by score (0-1 range).
 */
export interface OpenSetObjectDetectorModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.OPEN_SET_OBJECT_DETECTOR;
  prompts: string[];
  confidenceThreshold: number;
}

/**
 * Strawberry model configuration.
 * - `confidenceThreshold` gates detections by score (0-1 range).
 */
export interface StrawberryModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.STRAWBERRY;
  confidenceThreshold: number;
}

/**
 * Apple model configuration.
 * - `confidenceThreshold` gates detections by score (0-1 range).
 */
export interface AppleModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.APPLE;
  confidenceThreshold: number;
}

/**
 * Tomato model configuration.
 * - `confidenceThreshold` gates detections by score (0-1 range).
 * - `useColorForMaturity` toggles color-based maturity logic.
 */
export interface TomatoModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.TOMATO;
  confidenceThreshold: number;
  useColorForMaturity: boolean;
}

/**
 * Blueberry model configuration.
 * - `confidenceThreshold` gates detections by score (0-1 range).
 * - Uses dimensional formula for weight measurement.
 */
export interface BlueberryModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.BLUEBERRY;
  confidenceThreshold: number;
}

/**
 * Squash model configuration.
 * - `confidenceThreshold` gates detections by score (0-1 range).
 * - `hasWeightMeasurement` is inherited from the base and may be toggled per trait;
 *   weight estimates from the iOS side may arrive as `-1` sentinels when unavailable.
 */
export interface SquashModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.SQUASH;
  confidenceThreshold: number;
}

/**
 * Cucumber model configuration.
 * `hasWeightMeasurement` exists for parity with sibling models, but the
 * cucumber endpoint never returns weight today — deployed traits ship `false`.
 */
export interface CucumberModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.CUCUMBER;
  confidenceThreshold: number;
}

/**
 * Spinach keypoint model configuration.
 * - `confidenceThreshold` gates detections by score (0-1 range).
 * - Uses keypoint detection for plant counting.
 * - No weight estimation support.
 */
export interface SpinachKeypointModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.SPINACH_KEYPOINT;
  confidenceThreshold: number;
  hasWeightMeasurement: false;
}

/**
 * Arugula keypoint model configuration.
 * Identical in behavior to spinach keypoint — uses keypoint detection for plant counting.
 * No length/width or weight estimation support.
 */
export interface ArugulaKeypointModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.ARUGULA_KEYPOINT;
  confidenceThreshold: number;
  hasWeightMeasurement: false;
}

/**
 * Bisected lettuce model configuration.
 * Measures the head dimensions plus a descriptor-driven core length.
 * No weight estimation support.
 */
export interface BisectedLettuceModel extends ImageMeasurementModelBase {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.BISECTED_LETTUCE;
  hasWeightMeasurement: false;
  confidenceThreshold?: number;
  /**
   * Customer-specific lettuce core model to serve this config, e.g. "sakata".
   * Absent means the default model. Forwarded verbatim to the processor as the
   * `model_variant` form field; an unknown name is rejected server-side.
   */
  modelVariant?: string;
}

/**
 * No-model variant — manual annotation only, no automated detection.
 * Does NOT extend ImageMeasurementModelBase (no hasWeightMeasurement, no confidenceThreshold).
 */
export interface NoneModel {
  kind: typeof IMAGE_MEASUREMENT_MODEL_KINDS.NONE;
}

/**
 * Discriminated union of all image measurement model options.
 * Usage:
 *   if (opts.kind === "strawberry") {
 *     opts.confidenceThreshold // available
 *   }
 */
export type ImageMeasurementModelOptions =
  | OpenSetObjectDetectorModel
  | StrawberryModel
  | AppleModel
  | TomatoModel
  | BlueberryModel
  | SpinachKeypointModel
  | ArugulaKeypointModel
  | BisectedLettuceModel
  | SquashModel
  | CucumberModel
  | NoneModel;

// MARK: - Row Planted Stand Count Model Types

/**
 * Open set detector model for row-planted stand count.
 * Uses a text prompt to detect plants of any type.
 */
export interface RowPlantedStandCountOpenSetOptions {
  kind: "openSet";
  detectionPrompt: string;
  modelSensitivity?: number;  // 0-1 range
}

/**
 * Generic crop model for row-planted stand count.
 * Uses a pre-trained model for common crop types.
 */
export interface RowPlantedStandCountGenericCropOptions {
  kind: "genericCrop";
  modelId?: string;
  modelVersion?: string;
}

/**
 * Discriminated union of row-planted stand count model options.
 */
export type RowPlantedStandCountModelOptions =
  | RowPlantedStandCountOpenSetOptions
  | RowPlantedStandCountGenericCropOptions;

/**
 * Requirements for how an image should be captured for a measurement.
 * These constraints can be used by the UI to guide users before inference
 * and by validation to reject unsuitable images.
 */
export interface ImageCaptureRequirements {
  maxDistanceMeters?: number;
  minDistanceMeters?: number;
  maxPitchAngleDegrees?: number;
  minPitchAngleDegrees?: number;
}

// MARK: - Image Measurement Grading

export type GradingDimension = "length" | "width";

/**
 * A non-overlapping range on the chosen dimension. Convention: half-open
 * `[minMeters, maxMeters)` — a value exactly at `maxMeters` belongs to the
 * next bucket up. Open ends are encoded by the discriminator `kind`:
 *   - `lessThan`           = (-inf, maxMeters)
 *   - `between`            = [minMeters, maxMeters)
 *   - `greaterThanOrEqual` = [minMeters, +inf)
 */
export type GradingBucketRange =
  | { kind: "lessThan"; maxMeters: number }
  | { kind: "between"; minMeters: number; maxMeters: number }
  | { kind: "greaterThanOrEqual"; minMeters: number };

export interface GradingBucket {
  /** Stable identifier so React keys and ordinal color domains survive reorders. */
  id: string;
  /** Display label, e.g. "1A". Trimmed, 1-32 chars, unique within a trait. */
  label: string;
  range: GradingBucketRange;
}

/**
 * Optional grading configuration on an image measurement trait. When present,
 * every detected object is bucketed by `dimension` into exactly one bucket
 * (or "Ungraded" if it matches none). Grading is not persisted on entries —
 * it is computed on the fly from per-object CaptureMetadata.
 */
export interface ImageMeasurementGrading {
  dimension: GradingDimension;
  /** Render order, color order, and tie-break order all follow this array order. */
  buckets: GradingBucket[];
}

export interface EntryConfig {
  id?: string;
  name: string;
  description: string;
  isArchived: boolean;
  isSystem?: boolean;
  alwaysShowDescription?: boolean; // Show trait description during data collection
  type: EntryConfigType;
  orgId: string; // Organization ID - required for security rules
  metadata: Record<string, unknown>;
  createdAt: Timestamp;
  createdBy: DocumentReference;
  updatedAt: Timestamp;
  updatedBy: DocumentReference;
  transcriptionConfig?: Record<string, never>;
  supportedImageAnalysisTypes?: string[];
}

// Config interfaces for each entry type, extending EntryConfig
export interface TextConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.TEXT;
}

export interface NumberConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.NUMBER;
  numberConfig?: {
    wholeNumbersOnly?: boolean;
    min?: number;
    max?: number;
  };
}

export interface CategoryConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.CATEGORY;
  categoryConfig?: {
    choices: string[];
    allowMultipleSelections?: boolean; // Allow selecting multiple category options
  };
}

export interface DateConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.DATE;
  dateConfig?: {
    min?: string;
    max?: string;
  };
}

export interface TranscriptionConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.TRANSCRIPTION;
  transcriptionConfig?: Record<string, never>;
  supportedImageAnalysisTypes?: string[];
}

export interface PhotoConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.PHOTO;
  photoConfig?: {
    imageCaptureRequirements?: ImageCaptureRequirements;
  };
}

export interface BooleanConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.BOOLEAN;
}

export interface PercentageConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.PERCENTAGE;
}

export interface ImageMeasurementConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.IMAGE_MEASUREMENT;
  imageMeasurementConfig?: {
    imageMeasurementModelOptions: ImageMeasurementModelOptions;
    imageCaptureRequirements?: ImageCaptureRequirements;
    objectDetectionDefaultOptions?: ObjectDetectionDefaultOptions;
    grading?: ImageMeasurementGrading;
  };
}

export interface StandCountMeasurementConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.STAND_COUNT_MEASUREMENT;
  standCountMeasurementConfig?: {
    // Just the options object, not the full config
    imgMeasureConfig?: {
      imageMeasurementModelOptions: ImageMeasurementModelOptions;
      imageCaptureRequirements?: ImageCaptureRequirements;
      objectDetectionDefaultOptions?: ObjectDetectionDefaultOptions;
    };
    boxLengthMeters?: number;
    boxWidthMeters?: number;
  };
}

export interface LeafAreaIndexConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.LEAF_AREA_INDEX;
  leafAreaIndexConfig?: {
    /** Missing/false uses an on-screen box; true places a fixed-size AR box on the ground. */
    useFixedBoxSize?: boolean;
    defaultSensitivity?: number;
    boxLengthMeters?: number;
    boxWidthMeters?: number;
    imageCaptureRequirements?: ImageCaptureRequirements;
  };
}

export interface RulerConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.RULER;
  rulerConfig?: {
    minLengthMeters?: number;
    maxLengthMeters?: number;
  };
}

/**
 * Configuration for a timer (stopwatch) trait.
 * Mirrors iOS FSEntryConfig.TimerConfigOptions byte-for-byte.
 * The web app only authors this config and reads/displays/exports values —
 * it never starts/stops timers. `startStopOnBarcodeScan` is an iOS runtime
 * concern (start/stop the running stopwatch when a barcode is scanned).
 */
export interface TimerConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.TIMER;
  timerConfig?: {
    startStopOnBarcodeScan?: boolean;
  };
}

/**
 * Configuration for row-planted stand count measurements.
 * Used for AR walking measurements along crop rows.
 *
 * Parameter ranges (from iOS RowPlantedStandCountParams):
 * - expectedPlantLineCount: 1-6 (number of parallel plant rows)
 * - walkDistanceMetersTarget: 1-100m (target walk distance)
 * - minPlantSpacingMeters: 0.05-2.0m (5-200cm, threshold for merging duplicates)
 * - min/maxPlantDiameterMeters: 0.03-1.0m (3-100cm, expected plant diameter range)
 * - min/maxDetectionDistanceMeters: 0.3-5.0m (30-500cm, camera detection range)
 * - min/maxMeasurementAngleDegrees: -20 to 180 degrees (camera angle range)
 * - maxMeasurementDistanceMeters: 0.3-4.0m (30-400cm, max distance for diameter/health metrics)
 * - min/maxSizePercentOfImage: 0.1-10% / 1-100% (detection size filtering)
 */
export interface RowPlantedStandCountConfig extends EntryConfig {
  type: typeof ENTRY_CONFIG_TYPES.ROW_PLANTED_STAND_COUNT;
  rowPlantedStandCountConfig?: {
    modelOptions: RowPlantedStandCountModelOptions;
    expectedPlantLineCount?: number;
    walkDistanceMetersTarget?: number;
    isWalkDistanceRequired?: boolean;
    minPlantSpacingMeters?: number;
    minPlantDiameterMeters?: number;
    maxPlantDiameterMeters?: number;
    maxDetectionDistanceMeters?: number;
    minDetectionDistanceMeters?: number;
    minMeasurementAngleDegrees?: number;
    maxMeasurementAngleDegrees?: number;
    maxMeasurementDistanceMeters?: number;
    deduplicationRadiusMeters?: number;
    /** Maximum size of detection as percentage of image (1-100) */
    maxSizePercentOfImage?: number;
    /** Minimum size of detection as percentage of image (0.1-10) */
    minSizePercentOfImage?: number;
  };
}

// Discriminated union of all config types
export type EntryConfigUnion =
  | TextConfig
  | NumberConfig
  | CategoryConfig
  | DateConfig
  | TranscriptionConfig
  | PhotoConfig
  | BooleanConfig
  | PercentageConfig
  | ImageMeasurementConfig
  | StandCountMeasurementConfig
  | RowPlantedStandCountConfig
  | LeafAreaIndexConfig
  | RulerConfig
  | TimerConfig;
